/**
 * Beleg-Klärfälle (W12 doppelte Erstattung, W3 Lieferland, N4/R65 gescheiterte Belege, R66 Sondergebiet):
 * StoreOS erzeugt bewusst keinen Beleg und legt die Entscheidung der Leitung vor. Diese Karte steht auf der
 * Belege-Seite (alle offenen) und im Auftragsdetail (nur dieser Auftrag). Jede Lösung braucht einen Grund
 * (Pflicht, landet im Audit); geldrelevante Lösungen (Gutschrift erzeugen, bewusst ohne Beleg schließen,
 * Steuerfall freigeben) verlangen zusätzlich eine Bestätigung im zweiten Schritt.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { STEUERFAELLE } from '@storeos/contracts';
import { belegKlaerfaelle, belegKlaerfallLoesen, type BelegKlaerfall, type BelegKlaerfallArt, type BelegKlaerfallLoesung } from '../api/belege';
import { datumZeit, euro, STEUERFALL_TEXT } from './format';
import { Dialog, DialogFormular, Feld, Karte, useToast } from './ui';

export const BELEG_KLAERFALL_TEXT: Record<BelegKlaerfallArt, string> = {
  doppelte_erstattung: 'Doppelte Erstattung',
  land_unbekannt: 'Lieferland unbekannt oder Sondergebiet',
  erstattung_ueber_rechnung: 'Erstattung über dem Rechnungsbetrag',
  beleg_fehlgeschlagen: 'Beleg nicht erzeugt',
};

interface Loesung {
  aktion: BelegKlaerfallLoesung;
  /** Knopftext in der Liste. */
  knopf: string;
  /** Titel des Dialogs. */
  titel: string;
  /** Was passiert, in einem Satz für die Leitung. */
  erklaerung: string;
  /** Geldrelevant: Gutschrift, Verzicht auf einen Beleg oder Steuerfall → zweiter Schritt mit Bestätigung. */
  geld: boolean;
  steuerfall?: boolean;
}

const LOESUNGEN: Record<BelegKlaerfallArt, Loesung[]> = {
  doppelte_erstattung: [
    { aktion: 'kein_beleg', knopf: 'Kein zweiter Beleg', titel: 'Ohne zweite Gutschrift schließen', geld: true,
      erklaerung: 'Die Erstattung im Shop und die manuelle Gutschrift sind derselbe Geldfluss. Es bleibt bei der vorhandenen Gutschrift, StoreOS erzeugt keinen weiteren Beleg.' },
    { aktion: 'gutschrift_erzeugen', knopf: 'Gutschrift nachholen', titel: 'Zweite Gutschrift erzeugen', geld: true,
      erklaerung: 'Das Geld ist wirklich zweimal geflossen (im Shop und außerhalb). StoreOS erzeugt jetzt die Gutschrift zur Shop-Erstattung.' },
  ],
  erstattung_ueber_rechnung: [
    { aktion: 'gutschrift_rest', knopf: 'Gutschrift über den Rest', titel: 'Rest bis zum Rechnungsbetrag gutschreiben', geld: true,
      erklaerung: 'StoreOS schreibt gut, was zur Rechnung noch offen ist. Die Überzahlung bleibt ohne Beleg: vom Kunden zurückfordern oder mit der Steuerberatung buchen.' },
    { aktion: 'kein_beleg', knopf: 'Ohne Gutschrift schließen', titel: 'Ohne Gutschrift schließen', geld: true,
      erklaerung: 'Es entsteht keine Gutschrift, zum Beispiel weil die Erstattung im Shop ein Irrtum war und das Geld zurück ist. Der Klärfall wird mit deinem Grund geschlossen.' },
  ],
  beleg_fehlgeschlagen: [
    { aktion: 'erneut', knopf: 'Erneut versuchen', titel: 'Beleg erneut erzeugen', geld: false,
      erklaerung: 'Die Ursache ist behoben. StoreOS stellt den Auftrag neu ein und versucht es sofort. Scheitert er wieder, entsteht ein neuer Klärfall.' },
    { aktion: 'kein_beleg', knopf: 'Ohne Beleg schließen', titel: 'Bewusst ohne Beleg schließen', geld: true,
      erklaerung: 'Es entsteht kein Beleg. Nur, wenn wirklich keiner nötig ist; der Grund wird protokolliert.' },
  ],
  land_unbekannt: [
    { aktion: 'erledigt', knopf: 'Land korrigiert, Rechnung erzeugen', titel: 'Rechnung nach der Korrektur erzeugen', geld: false,
      erklaerung: 'Vorher die Lieferadresse im Auftrag korrigieren (Land, PLZ). StoreOS stellt den Rechnungsauftrag neu ein; bleibt das Land ungültig, kommt ein neuer Klärfall.' },
    { aktion: 'sondergebiet_freigeben', knopf: 'Sondergebiet freigeben', titel: 'Umsatzsteuerliches Sondergebiet freigeben', geld: true, steuerfall: true,
      erklaerung: 'Nur, wenn die Lieferadresse wirklich in einem Sondergebiet liegt (z. B. Helgoland, Büsingen). Den Steuerfall legt die Steuerberatung fest; die Rechnung rechnet damit und nennt den Lieferort.' },
  ],
};

/**
 * Karte mit den offenen Beleg-Klärfällen. `orderId`: nur dieser Auftrag (Auftragsdetail), sonst alle (Belege-Seite).
 * Ohne offene Fälle rendert die Karte nichts.
 */
export function BelegKlaerfaelle({ orderId }: { orderId?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['beleg-klaerfaelle'], queryFn: () => belegKlaerfaelle(true), refetchInterval: 60_000 });
  const [dialog, setDialog] = useState<{ k: BelegKlaerfall; l: Loesung } | null>(null);
  const faelle = (q.data?.klaerfaelle ?? []).filter((k) => k.status === 'offen' && (!orderId || k.order_id === orderId));
  if (!faelle.length) return null;
  const fertig = (text: string) => {
    setDialog(null);
    toast(text);
    for (const key of [['beleg-klaerfaelle'], ['beleg-hinweise'], ['belege'], ['dashboard'], ['auftrag']]) void qc.invalidateQueries({ queryKey: key });
  };
  return (
    <Karte titel={`Beleg-Klärfälle (${faelle.length})`} klasse="alarm">
      <p className="hinweis">StoreOS hat hier bewusst keinen Beleg erzeugt. Du entscheidest je Fall; jede Entscheidung braucht einen Grund und steht im Protokoll.</p>
      <ul className="liste-einfach beleg-klaerfaelle" data-testid="beleg-klaerfaelle">
        {faelle.map((k) => (
          <li key={k.id} data-testid="beleg-klaerfall" data-art={k.art}>
            <div className="bk-kopf">
              <strong>{BELEG_KLAERFALL_TEXT[k.art]}</strong>
              {!orderId && k.order_id && <Link to={`/auftraege/${k.order_id}`} className="mono">{k.bestellnummer}</Link>}
              {k.betrag_cents !== null && <span className="badge neutral">{euro(k.betrag_cents)}</span>}
              <span className="hinweis">seit {datumZeit(k.created_at)}</span>
            </div>
            <p className="bk-text">{k.text}</p>
            <div className="knopfreihe links">
              {LOESUNGEN[k.art].map((l) => (
                <button key={l.aktion} type="button" className={l.geld ? 'knopf-zweit klein' : 'knopf klein'} data-testid={`bk-loesung-${l.aktion}`} onClick={() => setDialog({ k, l })}>{l.knopf}</button>
              ))}
            </div>
          </li>
        ))}
      </ul>
      <Dialog offen={!!dialog} titel={dialog?.l.titel ?? ''} onSchliessen={() => setDialog(null)}>
        {dialog && <LoesungFormular key={`${dialog.k.id}-${dialog.l.aktion}`} k={dialog.k} l={dialog.l} onAbbrechen={() => setDialog(null)} onFertig={fertig} />}
      </Dialog>
    </Karte>
  );
}

function LoesungFormular({ k, l, onAbbrechen, onFertig }: { k: BelegKlaerfall; l: Loesung; onAbbrechen: () => void; onFertig: (text: string) => void }) {
  const [grund, setGrund] = useState('');
  const [steuerfall, setSteuerfall] = useState('');
  const [schritt, setSchritt] = useState<'eingabe' | 'bestaetigen'>('eingabe');
  const [bestaetigt, setBestaetigt] = useState(false);
  const pruefe = () => {
    if (grund.trim().length < 5) throw new Error('Grund ist Pflicht (mindestens 5 Zeichen).');
    if (l.steuerfall && !steuerfall) throw new Error('Steuerfall wählen (Entscheidung der Steuerberatung).');
  };
  const absenden = async () => {
    pruefe();
    if (l.geld && schritt === 'eingabe') { setSchritt('bestaetigen'); return; }
    if (l.geld && !bestaetigt) throw new Error('Bitte bestätigen: die Entscheidung ist geldrelevant.');
    const r = await belegKlaerfallLoesen(k.id, { aktion: l.aktion, grund: grund.trim(), steuerfall: l.steuerfall ? steuerfall : undefined });
    onFertig(r.document ? `Gutschrift ${r.document.nummer} über ${euro(r.document.brutto_cents)} erzeugt` : r.neu_eingestellt ? 'Auftrag neu eingestellt, der Beleg wird jetzt erzeugt' : 'Klärfall ohne Beleg geschlossen');
  };
  return (
    <DialogFormular absendenText={schritt === 'bestaetigen' ? `Ja, ${l.knopf.toLowerCase()}` : l.geld ? 'Weiter zur Bestätigung' : l.knopf} gefahr={l.geld && schritt === 'bestaetigen'} onAbbrechen={onAbbrechen} onAbsenden={absenden}>
      {schritt === 'eingabe' ? (
        <>
          <p>{l.erklaerung}</p>
          <p className="hinweis">{k.bestellnummer ? `Bestellung ${k.bestellnummer}: ` : ''}{k.text}</p>
          {l.steuerfall && (
            <Feld label="Steuerfall (Pflicht, Entscheidung der Steuerberatung)">
              <select value={steuerfall} onChange={(e) => setSteuerfall(e.target.value)} data-testid="bk-steuerfall">
                <option value="">– wählen –</option>
                {STEUERFAELLE.map((s) => <option key={s} value={s}>{STEUERFALL_TEXT[s] ?? s}</option>)}
              </select>
            </Feld>
          )}
          <Feld label="Grund (Pflicht)"><input value={grund} onChange={(e) => setGrund(e.target.value)} data-testid="bk-grund" placeholder="z. B. Doppelauszahlung geprüft, 10 € zurückgefordert" autoFocus /></Feld>
        </>
      ) : (
        <div className="warn-box" data-testid="bk-bestaetigung">
          <strong>Diese Entscheidung ist geldrelevant und lässt sich nicht zurücknehmen.</strong>
          <span>{l.titel}{k.betrag_cents !== null ? ` · Betrag im Klärfall ${euro(k.betrag_cents)}` : ''}{l.steuerfall ? ` · Steuerfall ${STEUERFALL_TEXT[steuerfall] ?? steuerfall}` : ''}</span>
          <span>Grund: {grund.trim()}</span>
          <label className="inline"><input type="checkbox" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} data-testid="bk-bestaetigen" /> Ich habe das geprüft und bestätige die Entscheidung.</label>
          <button type="button" className="knopf-flach" onClick={() => setSchritt('eingabe')}>Zurück zur Eingabe</button>
        </div>
      )}
    </DialogFormular>
  );
}
